import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import FileManager, { Item, Permissions, Toolbar } from 'devextreme-react/file-manager';
import notify from 'devextreme/ui/notify';
import './bookingDoc.scss';
import { ContextMenu, Popup, ScrollView } from 'devextreme-react';

interface BookingDocProps {
  id: string | number;
  user: {};
}

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
const GALLERY_CONCURRENCY = 4;

const BookingDoc: React.FC<BookingDocProps> = ({ id, user }) => {

  const baseUrl = process.env.REACT_APP_API_URL;

  const [bookingId, setBookingId] = useState(id);
  const [userData, setUserData] = useState<any>(user);
  const [popupVisible, setPopupVisible] = useState(false);
  const [imageItemToDisplay, setImageItemToDisplay] = useState<any>({});
  const [currentPath, setCurrentPath] = useState('Widescreen');
  const [filesAndFolders, setFilesAndFolders] = useState<any[]>([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [canUpload, setCanUpload] = useState(false);

  // --- "Preview all" gallery state ---
  const [galleryMode, setGalleryMode] = useState(false);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryImages, setGalleryImages] = useState<Array<{ name: string; url: string }>>([]);
  const [galleryTotalCount, setGalleryTotalCount] = useState(0);
  const [currentDirItems, setCurrentDirItems] = useState<any[]>([]);
  const [currentDirPath, setCurrentDirPath] = useState<string>('');
  // Tracks which gallery URLs we created ourselves (via createObjectURL) so we
  // only revoke those, not any pre-existing URLs that came straight from the API.
  const createdGalleryUrls = useRef<Set<string>>(new Set());

  useEffect(() => {
    getFilesAndFolders();
  }, [bookingId]);

  // Clean up the single-file preview object URL when component unmounts or when popup closes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Clean up any gallery object URLs on unmount
  useEffect(() => {
    return () => {
      createdGalleryUrls.current.forEach((url) => URL.revokeObjectURL(url));
      createdGalleryUrls.current.clear();
    };
  }, []);

  const hideImagePopup = useCallback(() => {
    setPopupVisible(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
    if (galleryMode) {
      createdGalleryUrls.current.forEach((url) => URL.revokeObjectURL(url));
      createdGalleryUrls.current.clear();
      setGalleryImages([]);
      setGalleryTotalCount(0);
      setGalleryMode(false);
    }
  }, [previewUrl, galleryMode]);

  const getFilesAndFolders = () => {
    if (bookingId !== undefined)
      axios.get(`${baseUrl}/api/Blob/list?folder=${bookingId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      })
        .then(response => setFilesAndFolders([response.data.data]))
        .catch(error => console.error('Error fetching files and folders:', error))
        .finally();
  };

  const toast = useCallback((message: any, type: any = 'success') => {
    notify({
      message: message,
    }, type, 3000);
  }, []);

  const isAdmin = () => ((userData && userData.role) && (userData.role.name === 'Admin'));
  const isDriver = () => ((userData && userData.role) && (userData.role.name === 'Driver'));
  const isCustomer = () => ((userData && userData.role) && (userData.role.name === 'Customer'));
  const isDisponent = () => ((userData && userData.role) && (userData.role.name === 'Disponent'));

  const displayDocPopup = useCallback(async (e: any) => {

    setIsLoading(true);
    setPopupVisible(true);

    const fileName = e.file.name;
    const fileExtension = fileName.split('.').pop().toLowerCase();
    let contentType = '';

    if (fileExtension === 'pdf') {
      contentType = 'application/pdf';
    } else if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
      contentType = `image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension}`;
    } else if (['mp4', 'webm'].includes(fileExtension)) {
      contentType = `video/${fileExtension}`;
    } else if (['mp3', 'wav'].includes(fileExtension)) {
      contentType = `audio/${fileExtension}`;
    } else {
      contentType = 'application/octet-stream';
    }

    setImageItemToDisplay({
      name: e.file.name,
      url: e.file.dataItem.url,
      type: contentType
    });

    try {
      const response = await axios.get(`${baseUrl}/api/Blob/download?folder=${e.file.parentPath}&fileName=${e.file.name}`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const fileBlob = new Blob([response.data], { type: contentType });
      const objectUrl = URL.createObjectURL(fileBlob);
      setPreviewUrl(objectUrl);
    } catch (error) {
      console.error('Error fetching file for preview:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // --- "Preview all" gallery logic ---

  const isImageFile = (name: string | undefined) =>
    !!name && IMAGE_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext));

  const openGallery = useCallback(async () => {
    const imageItems = currentDirItems.filter(
      (item: any) => !item.isDirectory && isImageFile(item.name)
    );

    if (imageItems.length === 0) {
      toast('Keine Bilder in diesem Ordner', 'info' as any);
      return;
    }

    setGalleryMode(true);
    setPopupVisible(true);
    setGalleryLoading(true);
    setGalleryImages([]);
    setGalleryTotalCount(imageItems.length);
    createdGalleryUrls.current.forEach((url) => URL.revokeObjectURL(url));
    createdGalleryUrls.current.clear();

    const results: Array<{ name: string; url: string } | undefined> = new Array(imageItems.length);
    let cursor = 0;

    const worker = async () => {
      while (cursor < imageItems.length) {
        const i = cursor++;
        const item = imageItems[i];
        try {
          // Always fetch via the authenticated download endpoint - same one used
          // by the single-file preview popup, which is known to work correctly.
          // (item.url isn't used here: it isn't a directly-loadable link for an
          // <img> tag without the Authorization header, which caused broken images.)
          const response = await axios.get(
            `${baseUrl}/api/Blob/download?folder=${currentDirPath || bookingId}&fileName=${item.name}`,
            {
              responseType: 'blob',
              headers: {
                Authorization: `Bearer ${localStorage.getItem('authToken')}`
              }
            }
          );
          const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'image/jpeg'
          });
          const url = URL.createObjectURL(blob);
          createdGalleryUrls.current.add(url);

          results[i] = { name: item.name, url };
          // Progressive display: update as each image finishes loading
          setGalleryImages(results.filter(Boolean) as Array<{ name: string; url: string }>);
        } catch (error) {
          console.error('Error loading image for gallery:', item.name, error);
        }
      }
    };

    const workerCount = Math.min(GALLERY_CONCURRENCY, imageItems.length);
    await Promise.all(Array.from({ length: workerCount }, worker));

    setGalleryLoading(false);
  }, [currentDirItems, currentDirPath, bookingId, baseUrl, toast]);

  const renderGallery = () => {
    if (galleryImages.length === 0 && galleryLoading) {
      return <div className="loading-container">Bilder werden geladen...</div>;
    }

    return (
      <div className="gallery-scroll-container">
        {galleryImages.map((img) => (
          <div className="gallery-item" key={img.name}>
            <div className="gallery-item-name">{img.name}</div>
            <img src={img.url} className="gallery-item-image" alt={img.name} />
          </div>
        ))}
        {galleryLoading && (
          <div className="loading-container">Weitere Bilder werden geladen...</div>
        )}
      </div>
    );
  };

  const renderPreview = () => {
    if (galleryMode) {
      return renderGallery();
    }

    if (isLoading) {
      return <div className="loading-container">Vorschau wird geladen...</div>;
    }

    const fileUrl = previewUrl || imageItemToDisplay.url;
    const fileName = imageItemToDisplay?.name?.toLowerCase();

    if (fileName?.endsWith('.pdf')) {
      return (
        <object
          data={fileUrl}
          type="application/pdf"
          width="100%"
          height="500px"
          className="pdf-preview"
        >
          <p>Ihr Browser unterstützt keine eingebetteten PDFs.
             Sie können <a href={fileUrl} target="_blank" rel="noopener noreferrer">die PDF-Datei herunterladen</a>.</p>
        </object>
      );
    } else if (fileName?.match(/\.(mp4|webm|ogg)$/)) {
      return (
        <video controls width="100%" height="auto">
          <source src={fileUrl} type={`video/${fileName.split('.').pop()}`} />
          Ihr Browser unterstützt das Video-Tag nicht.
        </video>
      );
    } else if (fileName?.match(/\.(mp3|wav|ogg)$/)) {
      return (
        <audio controls style={{ width: '100%' }}>
          <source src={fileUrl} type={`audio/${fileName.split('.').pop()}`} />
          Ihr Browser unterstützt das Audio-Tag nicht.
        </audio>
      );
    } else {
      return (
        <div className="image-preview-wrapper">
          <img src={fileUrl} className="photo-popup-image" alt={imageItemToDisplay?.name} />
        </div>
      )
    }
  };

  const galleryButtonDisabled = currentDirItems.filter(
    (i: any) => !i.isDirectory && isImageFile(i.name)
  ).length === 0;

  const previewAllButtonOptions = useMemo(() => ({
    text: 'Alle anzeigen',
    icon: 'image',
    onClick: openGallery,
    disabled: galleryButtonDisabled,
  }), [openGallery, galleryButtonDisabled]);

  const onCurrentDirectoryChanged = useCallback((e: any) => {
    setCanUpload(e.directory.name === 'Medien' || e.directory.name === 'Dokumente');

    // Track the current folder's raw items for the gallery button.
    // For an array/tree fileSystemProvider, the raw data node is available via
    // e.directory.dataItem. Adjust the `items` key below if your data uses a
    // different property name for a folder's children (e.g. `children`, `files`).
    const rawItems = e.directory?.dataItem?.items || e.directory?.dataItem?.children || [];
    setCurrentDirItems(rawItems);
    setCurrentDirPath(e.directory?.path || '');
  }, []);

  const downloadItem = useCallback((e: any) => {
    e.cancel = true;

    const downloadFile = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/Blob/download?folder=${e.item.parentPath}&fileName=${e.item.name}`, {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        const filename = e.item.dataItem.name;
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/octet-stream'
        });
        const downloadUrl = window.URL.createObjectURL(blob);

        const downloadLink = document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.setAttribute('download', filename);
        document.body.appendChild(downloadLink);
        downloadLink.click();

        setTimeout(() => {
          document.body.removeChild(downloadLink);
          window.URL.revokeObjectURL(downloadUrl);
        }, 100);

        toast('Datei erfolgreich heruntergeladen', 'success');
      } catch (e) {
        console.error(e);
        toast('Fehler beim Herunterladen der Datei', 'error');
      }
    };

    downloadFile();
  }, []);

  const onFileUploading = useCallback((e: any) => {
    if (!(e.destinationDirectory.name === "Dokumente" || e.destinationDirectory.name === "Medien")) return;
    e.cancel = true;
    const uploadFile = async () => {
      try {
        const file = e.fileData;
        const path = e.destinationDirectory?.path || '';

        const charToCount = '/';
        const count = path.split(charToCount).length - 1;

        if (count > 2) {
          toast('Der Pfad ist zu lang, bitte reduzieren Sie die Anzahl der Ordnerebenen', 'error');
          return;
        }

        const folder = e.destinationDirectory.path
        const formData = new FormData();
        formData.append('file', file, file.name);

        const response = await axios.post(`${baseUrl}/api/Blob/upload?folder=${folder}`, formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authToken')}`,
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent: any) => {
            }
          }
        );

        toast('Die Datei wurde erfolgreich hochgeladen', 'success');
        getFilesAndFolders();

        if (e.onUploadCompleted) {
          e.onUploadCompleted();
        }

      } catch (error) {
        console.error('Upload error:', error);
        toast('Fehler beim Hochladen der Datei', 'error');
      }
    };

    uploadFile();
  }, []);

  return (
    <ScrollView>
      <FileManager
        height={'100%'}
        currentPath={currentPath}
        fileSystemProvider={filesAndFolders}
        onFileUploading={onFileUploading}
        onSelectedFileOpened={displayDocPopup}
        onItemDownloading={downloadItem}
        onCurrentDirectoryChanged={onCurrentDirectoryChanged}>
        <Toolbar>
          <Item name="showNavPane" />
          <Item name="create" />
          <Item name="upload" />
          <Item name="switchView" />
          <Item name="separator" location="after" />
          <Item name="refresh" />
          <Item widget="dxButton" options={previewAllButtonOptions} location="after" />
        </Toolbar>
        <Permissions
          create={false}
          copy={false}
          move={false}
          delete={false}
          rename={false}
          upload={canUpload && (isAdmin() || isDisponent())}
          download={true}>
        </Permissions>
        <ContextMenu><Item /></ContextMenu>
      </FileManager>
      <Popup
        maxHeight={'100vh'}
        hideOnOutsideClick={false}
        showCloseButton={true}
        title={galleryMode ? `Alle Bilder (${galleryImages.length}/${galleryTotalCount})` : imageItemToDisplay.name}
        visible={popupVisible}
        onHiding={hideImagePopup}
        className="photo-popup-content">

        {renderPreview()}
      </Popup>
    </ScrollView>
  );
};

export default BookingDoc;
